// editor-chrome — the DEFAULT editor surface's host chrome (clientside-teacher-review,
// task 1.5). This is the wiring that used to live inline in main.js: the owner-only
// Save, the in-app Remix, and the Share / Download / Settings / Assets editing-loop
// controls. Extracted verbatim into a per-surface module so main.js can dispatch on
// body[data-surface] across the editor / review / assignment / viewonly surfaces while
// editor-core + Ace load once. The default (pgzero) editor path is unchanged.
//
//   wire(ctx) — ctx = { core, payload, trinketId, clientsideMode, isOwner, editorApi, doc }
// editor-core is already constructed and loaded by main.js; this only wires the chrome.

import { wireAssets } from './assets-panel.js';
import { registerSaveHandler } from './editor-shortcuts.js';

export function wire(ctx) {
  var core = ctx.core;
  var payload = ctx.payload;
  var trinketId = ctx.trinketId;
  var clientsideMode = ctx.clientsideMode;
  var editorApi = ctx.editorApi;
  var document = ctx.doc;

  // Owner-only Save: the control is rendered only for owners (template-gated),
  // and the endpoint is owner-gated server-side. Save persists the live buffer.
  var saveBtn = document.getElementById('runner-save');
  var saveStatus = document.getElementById('runner-save-status');
  function setSaveStatus(text, isError) {
    if (!saveStatus) { return; }
    saveStatus.textContent = text || '';
    saveStatus.classList.toggle('runner-error', !!isError);
  }
  // ONE save path shared by the button and the Ctrl-S shortcut, so both reuse the same
  // status wording and the same re-entrancy guard (the button disables itself while a save
  // is in flight, which also stops a held Ctrl-S stacking requests).
  function doSave() {
    if (!saveBtn || saveBtn.disabled) { return; }
    saveBtn.disabled = true;
    setSaveStatus('Saving…', false);
    editorApi.saveProject(trinketId, core.getFiles())
      .then(function () { setSaveStatus('Saved', false); })
      .catch(function (err) { setSaveStatus('Save failed', true); console.error(err); })
      .then(function () { saveBtn.disabled = false; });
  }
  if (saveBtn) {
    saveBtn.addEventListener('click', doSave);
    // Owner-only by construction: the Save control is template-gated on isOwner, so a
    // non-owner registers nothing and Ctrl-S is swallowed with no status change rather
    // than firing a call the server would reject.
    registerSaveHandler(doSave);
  }

  // Optional Remix control (in-app editor top bar only). Forks the live buffer
  // into the user's library, then navigates to the new copy. Absent on the embed
  // surface, so this is a no-op there.
  var remixBtn = document.getElementById('app-remix');
  var remixStatus = document.getElementById('app-remix-status');
  function setRemixStatus(text, isError) {
    if (!remixStatus) { return; }
    remixStatus.textContent = text || '';
    remixStatus.style.color = isError ? '#f9a8a3' : '';
  }
  if (remixBtn) {
    remixBtn.addEventListener('click', function () {
      remixBtn.disabled = true;
      setRemixStatus('Remixing…', false);
      editorApi.remixProject(trinketId, core.getFiles())
        .then(function (result) {
          var copy = result && result.data;
          if (copy && copy.shortCode) {
            // Land on the canonical client-side route for this language
            // (clientside-multilang-editor): python/pygame keep their own route,
            // everything else is pgzero.
            var langPath = clientsideMode === 'python' ? '/python/'
              : clientsideMode === 'pygame' ? '/pygame/' : '/pgzero/';
            window.location.href = langPath + copy.shortCode;
            return;
          }
          setRemixStatus('Remixed', false);
          remixBtn.disabled = false;
        })
        .catch(function (err) {
          setRemixStatus('Remix failed', true);
          remixBtn.disabled = false;
          console.error(err);
        });
    });
  }

  // --- Editing-loop chrome (clientside-editor-parity) -----------------------
  // Host chrome over existing endpoints (design D1). All controls are owner-gated
  // in the template (mirroring Save); when a control is absent its wiring no-ops.
  // a11y is the exception — available to everyone and handled in editor-core.

  // Small modal helpers: toggle the [hidden] backdrop and focus the first field.
  function openModal(modal, focusEl) {
    if (!modal) { return; }
    modal.hidden = false;
    if (focusEl && focusEl.focus) { focusEl.focus(); }
  }
  function closeModal(modal) {
    if (modal) { modal.hidden = true; }
  }
  function wireModalDismiss(modal, closeBtn) {
    if (closeBtn) { closeBtn.addEventListener('click', function () { closeModal(modal); }); }
    if (modal) {
      // Click the backdrop (not the dialog) to dismiss; Esc closes too.
      modal.addEventListener('click', function (e) { if (e.target === modal) { closeModal(modal); } });
      modal.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' || e.keyCode === 27) { closeModal(modal); }
      });
    }
  }

  // Share: surface the canonical /pgzero/{shortCode} URL in a copyable field. No
  // backend, no cross-origin subresource (isolation/COEP preserved).
  // canvas-only-share: two link shapes, chosen by the #share-mode radios in the
  // modal — "Just the canvas" (default, ?outputOnly=1, the run-only page) and
  // "Canvas and code" (the bare editor URL). Purely client-side; no request.
  var shareBtn = document.getElementById('runner-share');
  var shareModal = document.getElementById('share-modal');
  var shareUrl = document.getElementById('share-url');
  var shareCopy = document.getElementById('share-copy');
  var shareStatus = document.getElementById('share-status');
  var shareCanvasRadio = document.getElementById('share-mode-canvas');
  var shareFullRadio = document.getElementById('share-mode-full');
  // Canvas-only unless the owner has explicitly picked "Canvas and code" — and
  // still canvas-only if the radios are absent (private-by-default).
  function shareCanvasOnly() {
    return !(shareFullRadio && shareFullRadio.checked);
  }
  function refreshShareUrl() {
    if (shareUrl) {
      shareUrl.value = window.location.origin +
        editorApi.shareLink(trinketId, clientsideMode, shareCanvasOnly());
    }
  }
  if (shareBtn && shareModal) {
    shareBtn.addEventListener('click', function () {
      // Reset to the private-by-default choice on every open.
      if (shareCanvasRadio) { shareCanvasRadio.checked = true; }
      refreshShareUrl();
      if (shareStatus) { shareStatus.textContent = ''; }
      openModal(shareModal, shareUrl);
      if (shareUrl && shareUrl.select) { shareUrl.select(); }
    });
    // Switching the mode rebuilds the link, so a stale "Copied" (for the link the
    // field no longer shows) must clear with it.
    [shareCanvasRadio, shareFullRadio].forEach(function (radio) {
      if (!radio) { return; }
      radio.addEventListener('change', function () {
        refreshShareUrl();
        if (shareStatus) { shareStatus.textContent = ''; }
      });
    });
    wireModalDismiss(shareModal, document.getElementById('share-close'));
    if (shareCopy) {
      shareCopy.addEventListener('click', function () {
        if (shareUrl && shareUrl.select) { shareUrl.select(); }
        var done = function (ok) {
          if (shareStatus) { shareStatus.textContent = ok ? 'Copied' : 'Press Ctrl+C to copy'; }
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(shareUrl ? shareUrl.value : '')
            .then(function () { done(true); }, function () { done(false); });
        } else {
          try { done(document.execCommand('copy')); } catch (e) { done(false); }
        }
      });
    }
  }

  // Download: single file (no assets) -> a bare .py client-side; multi-file or
  // assets -> a zip via the existing endpoint. Always the LIVE buffers.
  var downloadBtn = document.getElementById('runner-download');
  if (downloadBtn) {
    downloadBtn.addEventListener('click', function () {
      downloadBtn.disabled = true;
      editorApi.downloadProject({
        filename: document.body.getAttribute('data-project-name') || '',
        assetBaseUrl: payload.assetBaseUrl,
        assetManifest: payload.assetManifest
      }, core.getFiles())
        .catch(function (err) { console.error(err); })
        .then(function () { downloadBtn.disabled = false; });
    });
  }

  // Settings: edit name + description via the existing update routes (owner-gated
  // server-side). The elements are resolved HERE, above the rename chrome, because the
  // inline rename talks to them (it mirrors its status into #settings-status and
  // serialises against this modal's Save); the modal's own wiring is further down and
  // calls back into the rename helpers in turn.
  var settingsBtn = document.getElementById('runner-settings');
  var settingsModal = document.getElementById('settings-modal');
  var settingsName = document.getElementById('settings-name');
  var settingsDescription = document.getElementById('settings-description');
  var settingsSave = document.getElementById('settings-save');
  var settingsStatus = document.getElementById('settings-status');
  function setSettingsStatus(text, isError) {
    if (!settingsStatus) { return; }
    settingsStatus.textContent = text || '';
    settingsStatus.classList.toggle('runner-error', !!isError);
  }

  // --- Inline project rename (inline-project-rename) -------------------------
  // Click the project name (or the pencil beside it) in the toolbar to rename it.
  //
  // THIS MODULE RUNS ON FIVE PAGES. main.js resolves body[data-surface] || 'editor',
  // and three runner-smoke harness pages plus the /embed/{lang}/{projectId} editor
  // emit no data-surface, so they all land here. Only the in-app editor has the header
  // name, the breadcrumb and the settings modal; the harness pages have no
  // data-project-name attribute AT ALL, so the read is null. Every read below is
  // `|| ''` and every write is guarded — a bare string operation at wire time would
  // throw inside main.js's loadProject().then(...), so window.__editorCore would never
  // publish and the editor would break on EVERY surface, not just that one.
  var docBody = document.body;
  var nameEl = document.querySelector('.app-trinket-name');
  var renameBtn = document.getElementById('project-rename');
  var renameInput = document.getElementById('project-rename-input');
  var renameStatus = document.getElementById('project-rename-status');
  var crumbEl = document.querySelector('.pcbreadcrumb-current');
  // Emitted by the in-app template from the same expressions it renders, so the JS and
  // the server-rendered markup cannot drift. Absent on the other four pages.
  var untitledLabel = (docBody && docBody.getAttribute('data-project-untitled')) || '';
  var langLabel = docBody && docBody.getAttribute('data-lang-label');
  var siteName = docBody && docBody.getAttribute('data-site-name');

  var NAME_MAX = 50;                 // mirrors the route's Joi .max(50) and maxlength="50"
  var RENAME_SUCCESS_MS = 3000;      // success announces briefly, then clears
  var RENAME_ERROR_MS = 10000;       // failures persist, with a backstop so they can't stick
  var RENAME_MIN_WIDTH = '18ch';     // usable minimum width for the input

  var renameStatusTimer = null;
  var renameStatusText = '';
  var renameStatusIsError = false;
  var settingsOwnsStatus = false;    // the modal is writing its own #settings-status
  var settingsNameDirty = false;     // the user has typed into #settings-name
  var renamePending = false;         // an inline rename request is in flight
  var modalSavePending = false;      // a settings save is in flight
  var editing = false;               // the inline input is open

  // Trimming lives in the SHARED path, so the settings modal gains it too.
  function normalizeName(value) {
    return typeof value === 'string' ? value.trim() : '';
  }
  // ONE length check, shared by both paths. Over-length names are refused, never
  // truncated: maxlength constrains typing only, and the remix path writes an UNCAPPED
  // `parent.name + ' Remix'`, so names beyond the cap already exist in the database.
  function nameTooLong(value) {
    return normalizeName(value).length > NAME_MAX;
  }
  // The RAW stored name — '' for an unnamed project, and null (hence `|| ''`) on the
  // harness pages. NEVER read the header span: for an unnamed project it displays the
  // "Untitled {lang} project" placeholder, so committing it would persist the
  // placeholder as the real name across the library card, breadcrumb, download filename
  // and page title.
  function rawProjectName() {
    return (docBody && docBody.getAttribute('data-project-name')) || '';
  }

  // ONE writer for every surface that shows or derives from the name. Each target is
  // written only if present. Which value each one gets is load-bearing: targets 1-3 show
  // the DISPLAY value (the untitled label when the name is empty), targets 4-5 hold the
  // RAW value — they are the source of truth for the NEXT rename and for the download
  // filename, so writing the placeholder into them is the same bug by the back door.
  function syncProjectName(raw, opts) {
    var value = normalizeName(raw);
    var shown = value || untitledLabel;
    if (nameEl) { nameEl.textContent = shown; }                              // 1 header
    if (crumbEl) { crumbEl.textContent = shown; }                            // 3 breadcrumb
    // 4 settings field. Skipped on a ROLLBACK when the user has typed into it, so a name
    // they are still editing is not overwritten (and then re-sent by the next save). The
    // discriminator is DIRTY, not focused: openModal focuses this field unconditionally.
    if (settingsName && !(opts && opts.keepDirtySettingsName && settingsNameDirty)) {
      settingsName.value = value;
    }
    if (docBody) { docBody.setAttribute('data-project-name', value); }       // 5 truth
    // 2 page title, composed by the SERVER's own rule — whose fallback is the LANGUAGE
    // label, not the untitled label the header and breadcrumb use. Written even for an
    // empty name. With the attributes absent (the embed surface) we cannot compose it
    // correctly, so we leave it alone rather than guess a format.
    if (langLabel && siteName) { document.title = (value || langLabel) + ' · ' + siteName; }
  }

  function clearRenameStatusTimer() {
    if (renameStatusTimer) { clearTimeout(renameStatusTimer); renameStatusTimer = null; }
  }
  // Rename status has its OWN element. #runner-save-status is written by setSaveStatus
  // and shared with the code Save button, so reusing it would make a rename failure read
  // "Save failed" and let the two clobber each other.
  function setRenameStatus(text, isError, autoClearMs) {
    renameStatusText = text || '';
    renameStatusIsError = !!isError;
    clearRenameStatusTimer();
    if (renameStatus) {
      renameStatus.textContent = renameStatusText;
      renameStatus.classList.toggle('is-shown', !!renameStatusText);
      renameStatus.classList.toggle('runner-error', renameStatusIsError);
    }
    mirrorRenameStatus();
    if (renameStatusText && autoClearMs) {
      renameStatusTimer = setTimeout(function () { setRenameStatus('', false); }, autoClearMs);
    }
  }
  // The toolbar region sits at z-index 40, below .modal-backdrop's 50 — so the only
  // moment a user can see the settings Save disabled by a pending rename is exactly the
  // moment its stated cause is hidden behind the backdrop. Mirror pending, success AND
  // failure. Scoped to the INLINE rename: a modal-initiated save writes its own messages,
  // and those always win.
  function mirrorRenameStatus() {
    if (!settingsModal || settingsModal.hidden || settingsOwnsStatus) { return; }
    setSettingsStatus(renameStatusText, renameStatusIsError);
  }
  // The modal takes ownership of #settings-status. Cancelling the success auto-clear is
  // required: it would otherwise blank the modal's freshly-written result ~3s later.
  function settingsTakesStatus() {
    settingsOwnsStatus = true;
    clearRenameStatusTimer();
    renameStatusText = '';
    renameStatusIsError = false;
    if (renameStatus) {
      renameStatus.textContent = '';
      renameStatus.classList.remove('is-shown');
      renameStatus.classList.remove('runner-error');
    }
  }

  function anyModalOpen() {
    return !!document.querySelector('.modal-backdrop:not([hidden])');
  }
  function syncModalMarkers() {
    if (docBody && docBody.classList) { docBody.classList.toggle('is-modal-open', anyModalOpen()); }
    // Both this region and #settings-status are aria-live="polite" and the mirror writes
    // the same text to both, so suppress this one WHILE THE SETTINGS MODAL IS OPEN only.
    // Deliberately narrower than the inert rule below: share, assets and the record panel
    // have no mirror, so silencing it behind them would leave a rename that was ALREADY
    // in flight failing both invisibly and unannounced.
    var settingsOpen = !!(settingsModal && !settingsModal.hidden);
    if (renameStatus) { renameStatus.setAttribute('aria-hidden', settingsOpen ? 'true' : 'false'); }
  }
  // Four overlays carry .modal-backdrop — #settings-modal, #share-modal, #assets-modal
  // and #challenge-record-panel — but they are opened by THREE different code paths:
  // this file's openModal covers only share + settings, assets-panel.js defines its own
  // local one, and challenge-record-chrome.js (a separately imported module that cannot
  // see this file's helpers) sets .hidden directly. So the body marker is driven by
  // observing the attribute itself, and the trigger gate re-queries live. Flagging inside
  // openModal is the obvious implementation and it silently misses half the overlays.
  var backdrops = document.querySelectorAll ? document.querySelectorAll('.modal-backdrop') : [];
  if (typeof window !== 'undefined' && window.MutationObserver && backdrops.length) {
    var backdropObserver = new window.MutationObserver(syncModalMarkers);
    Array.prototype.forEach.call(backdrops, function (el) {
      backdropObserver.observe(el, { attributes: true, attributeFilter: ['hidden'] });
    });
  }
  syncModalMarkers();

  // At most ONE rename request in flight. The inline path is optimistic and the modal
  // path pessimistic, so interleaved they could land in either order and the last writer
  // would win with the wrong value. This is also what LIFTS the block when an inline
  // rename that was pending at modal-open time resolves.
  function syncSettingsSaveEnabled() {
    if (settingsSave) { settingsSave.disabled = renamePending || modalSavePending; }
  }

  // Width: max(measured name width, a usable minimum), capped at the 40ch the name span
  // itself uses. Accepted trade-off — for a very short name this shifts Save / ⋯ Project
  // once; usability beats a perfectly static row. Applied through max-width, because the
  // input's flex BASE must stay zero (see the CSS note on line-breaking).
  function sizeRenameInput() {
    if (!renameInput) { return; }
    var measured = 0;
    if (nameEl && nameEl.getBoundingClientRect) {
      // + the input's own horizontal padding and border.
      measured = Math.ceil(nameEl.getBoundingClientRect().width) + 28;
    }
    renameInput.style.maxWidth = 'min(max(' + measured + 'px, ' + RENAME_MIN_WIDTH + '), 40ch)';
  }

  function focusRenameBtn() {
    // The pencil is restored by closeRename() BEFORE this runs: .focus() on a hidden
    // element silently no-ops.
    if (renameBtn && renameBtn.focus) { renameBtn.focus(); }
  }

  function openRename() {
    if (!renameBtn || !renameInput || !nameEl) { return; }
    if (editing || renameBtn.disabled) { return; }
    // Inert behind ANY of the four overlays. None of them installs a focus trap, so a
    // keyboard user could otherwise Tab to the pencil behind a translucent backdrop, open
    // an invisible input with an invisible status region, type, then click a control
    // inside the visible dialog — and BLUR COMMITS, firing a real rename with a value the
    // user never knowingly entered. That is wrong persisted data, not an a11y wart.
    if (anyModalOpen()) { return; }
    if (renamePending || modalSavePending) {
      // Never a silent refusal.
      setRenameStatus('Still saving the last name change. Try again in a moment.', false);
      return;
    }
    setRenameStatus('', false);              // entering edit mode clears a previous failure
    sizeRenameInput();                       // measure BEFORE the span is hidden
    renameInput.value = rawProjectName();    // the RAW name: genuinely empty when unnamed
    renameInput.placeholder = untitledLabel; // the placeholder is NEVER a value
    renameInput.removeAttribute('aria-invalid');
    renameInput.removeAttribute('aria-describedby');
    editing = true;
    nameEl.hidden = true;
    renameInput.hidden = false;
    renameBtn.classList.add('is-editing');
    renameInput.focus();
    if (renameInput.select) { renameInput.select(); }
  }

  function closeRename() {
    editing = false;
    if (renameInput) { renameInput.hidden = true; }
    if (nameEl) { nameEl.hidden = false; }
    if (renameBtn) { renameBtn.classList.remove('is-editing'); }
  }

  function renameFailureText(err) {
    var kind = err && err.kind;
    if (kind === 'validation') {
      return err.detail ? ('The project was not renamed: ' + err.detail)
        : 'The server did not accept that project name.';
    }
    if (kind === 'network') { return 'No connection, so the project was not renamed.'; }
    if (err && (err.status === 401 || err.status === 403)) {
      return 'You need to be signed in as the owner to rename this project.';
    }
    return 'Something went wrong, so the project was not renamed.';
  }
  function descriptionFailureText(err) {
    var kind = err && err.kind;
    if (kind === 'validation') {
      return err.detail ? ('The instructions were not saved: ' + err.detail)
        : 'The server did not accept those instructions.';
    }
    if (kind === 'network') { return 'No connection, so the instructions were not saved.'; }
    if (err && (err.status === 401 || err.status === 403)) {
      return 'You need to be signed in as the owner to edit this project.';
    }
    return 'Something went wrong, so the instructions were not saved.';
  }

  function releaseRename() {
    renamePending = false;
    syncSettingsSaveEnabled();
  }

  function sendRename(previous, next) {
    renamePending = true;
    syncSettingsSaveEnabled();
    setRenameStatus('Renaming the project…', false);
    // Optimistic: every surface is written first — which is what makes "open settings
    // mid-rename and see the new value" hold — then rolled back if the save fails.
    syncProjectName(next);
    editorApi.updateName(trinketId, next).then(function () {
      setRenameStatus('Project renamed.', false, RENAME_SUCCESS_MS);
    }, function (err) {
      syncProjectName(previous, { keepDirtySettingsName: true });
      setRenameStatus(renameFailureText(err), true, RENAME_ERROR_MS);
      console.error(err);
    }).then(releaseRename, releaseRename);   // both arms: the flag must never stick
  }

  function commitRename(viaEnter) {
    // One edit produces at most one request: closeRename() clears `editing` before the
    // blur that Enter provokes can arrive, and every entry point returns early on it.
    if (!editing || !renameInput) { return; }
    var previous = rawProjectName();
    var next = normalizeName(renameInput.value);

    // ORDER IS LOAD-BEARING. A remixed project can already carry a >50-character name, so
    // an UNCHANGED commit must simply close: checking length first would show a blocking
    // error for a rename the user never made, on a name the app itself created.
    if (next === previous) {                                   // (1) unchanged vs the RAW name
      closeRename();
      if (viaEnter) { focusRenameBtn(); }
      return;
    }
    if (!next) {                                               // (2) blank after trimming
      closeRename();
      if (viaEnter) { focusRenameBtn(); }
      return;
    }
    if (nameTooLong(next)) {                                   // (3) over the length cap
      if (viaEnter) {
        // Deliberate commit: keep the input open so the name can be shortened in place.
        renameInput.setAttribute('aria-invalid', 'true');
        renameInput.setAttribute('aria-describedby', 'project-rename-status');
        setRenameStatus('Project names can be up to ' + NAME_MAX +
          ' characters. Shorten it, then press Enter.', true, RENAME_ERROR_MS);
        return;
      }
      // The user walked away: revert and close rather than leave an abandoned input and
      // an error occupying the toolbar, reachable only by re-focusing an input they left.
      closeRename();
      setRenameStatus('Project names can be up to ' + NAME_MAX +
        ' characters. The previous name was kept.', true, RENAME_ERROR_MS);
      return;
    }
    closeRename();
    if (viaEnter) { focusRenameBtn(); }
    sendRename(previous, next);
  }

  if (renameBtn && renameInput && nameEl) {
    renameBtn.addEventListener('click', function () { openRename(); });
    // Click-to-edit on the name is gated on the OWNER marker the template renders — the
    // span itself renders for everyone, so a non-owner or anonymous visitor must get no
    // affordance. The span carries no role, tabindex or aria-label (an aria-label would
    // override its contents and drop the project name out of the accessibility tree); the
    // keyboard path is the pencil button.
    if (nameEl.classList && nameEl.classList.contains('is-project-owner')) {
      nameEl.addEventListener('click', function () { openRename(); });
    }
    renameInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.keyCode === 13) {
        e.preventDefault();
        commitRename(true);
        return;
      }
      if (e.key === 'Escape' || e.keyCode === 27) {
        e.preventDefault();
        e.stopPropagation();          // don't also trip the ⋯ menu / modal Escape handlers
        closeRename();
        setRenameStatus('', false);   // an Escape cancel dismisses a lingering failure
        focusRenameBtn();
      }
    });
    // Blur commits — but focus is NOT restored afterwards: a blur commit happens because
    // the user deliberately clicked elsewhere (Run, Save, ⋯ Project, into Ace), and
    // yanking focus back would take their next keystrokes with it.
    renameInput.addEventListener('blur', function () { commitRename(false); });
    // The chrome has wired: add the second half of the cursor gate and enable the pencil
    // the template rendered disabled. main.js wires inside loadProject().then(...), and
    // its failure branch never wires — so a dead editor never shows a live affordance.
    nameEl.classList.add('is-project-rename-ready');
    renameBtn.disabled = false;
  }

  // The settings save, with the two legs resolved INDEPENDENTLY.
  function saveSettings() {
    var name = normalizeName(settingsName ? settingsName.value : '');
    var description = settingsDescription ? settingsDescription.value : '';
    var nameRefused = nameTooLong(name);
    modalSavePending = true;
    settingsTakesStatus();
    syncSettingsSaveEnabled();
    setSettingsStatus('Saving…', false);

    // Folding each leg into a result object (rather than Promise.all + one .catch) is
    // what lets the name leg report specifically, keeps a description rejection from
    // being reported as a name failure, and leaves no unhandled rejection.
    var namePromise = nameRefused
      ? Promise.resolve({ skipped: true })
      : editorApi.updateName(trinketId, name).then(
        function () { return { ok: true }; },
        function (err) { console.error(err); return { ok: false, err: err }; });
    // Sent EVEN WHEN the name is refused for length. Today that teacher's instructions
    // edit IS persisted (the name leg benign-200s and is misreported as success), so
    // suppressing it would be a regression this change introduces.
    var descPromise = editorApi.updateDescription(trinketId, description).then(
      function () { return { ok: true }; },
      function (err) { console.error(err); return { ok: false, err: err }; });

    Promise.all([namePromise, descPromise]).then(function (results) {
      var nameRes = results[0];
      var descRes = results[1];
      // PESSIMISTIC: the five surfaces are written only AFTER the name leg actually
      // succeeded. Only the inline path is optimistic. Writing them where the old code
      // did — inside a .then that resolves on a benign 200 — would paint a phantom name
      // across the whole app on exactly the failure this change exists to catch.
      if (nameRes.ok) { syncProjectName(name); }
      setSettingsStatus(settingsSaveText(nameRes, descRes), !(nameRes.ok && descRes.ok));
      // Re-enabled in EVERY outcome on both legs; the modal always stays open. Both
      // arms are wired so a throw while composing the message cannot strand Save.
    }).then(releaseSettingsSave, releaseSettingsSave);
  }
  function releaseSettingsSave() {
    modalSavePending = false;
    syncSettingsSaveEnabled();
  }
  // Never "Saved" unless BOTH legs succeeded; when one fails, say which.
  function settingsSaveText(nameRes, descRes) {
    if (nameRes.ok && descRes.ok) { return 'Saved'; }
    var parts = [];
    if (nameRes.ok) {
      parts.push('Name saved.');
    } else if (nameRes.skipped) {
      parts.push('The name was not saved: project names can be up to ' + NAME_MAX + ' characters.');
    } else {
      parts.push(renameFailureText(nameRes.err));
    }
    parts.push(descRes.ok ? 'Instructions saved.' : descriptionFailureText(descRes.err));
    return parts.join(' ');
  }

  if (settingsBtn && settingsModal) {
    settingsBtn.addEventListener('click', function () {
      settingsOwnsStatus = false;
      setSettingsStatus('', false);
      // Repopulate from the source of truth rather than trusting the server-rendered
      // value, so an earlier inline rename cannot be reverted by opening and saving here.
      if (settingsName) { settingsName.value = rawProjectName(); }
      settingsNameDirty = false;
      openModal(settingsModal, settingsName);
      syncModalMarkers();
      syncSettingsSaveEnabled();
      mirrorRenameStatus();   // AFTER the clear above, or a pending rename's reason is lost
    });
    wireModalDismiss(settingsModal, document.getElementById('settings-close'));
    if (settingsName) {
      settingsName.addEventListener('input', function () { settingsNameDirty = true; });
    }
    if (settingsSave) {
      settingsSave.addEventListener('click', function () { saveSettings(); });
    }
  }
  syncSettingsSaveEnabled();

  // Assets panel (clientside-media-assets): owner-gated upload / list / remove of image
  // & sound media. Shared with the student assignment surface (assets-panel.js); the
  // controls are template-gated (owner AND features.assets), so it no-ops when absent.
  wireAssets(ctx);
}
